import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import { IconTile, PageHeader, Pill } from "../components/ui";
import { useTranslation } from "react-i18next";
import { api } from "../api/axios";

function normalizeFeatures(features) {
  if (!features) return {};
  if (typeof features === "object") return features;
  try {
    return JSON.parse(features);
  } catch {
    return {};
  }
}

function featureLabel(key) {
  const map = {
    font: "הגדלת/הקטנת טקסט",
    contrast: "ניגודיות גבוהה",
    links: "הדגשת קישורים",
    stopAnimations: "עצירת אנימציות",
    invertColors: "היפוך צבעים",
    readableFont: "פונט קריא",
    readLine: "קו קריאה",
    lineHeight: "שליטה במרווח שורות",
    letterSpacing: "שליטה במרווח אותיות",
    statement: "הצהרת נגישות (דרך הדשבורד)",
    aiAudit: "בדיקת נגישות חכמה (AI Audit)",
    aiAltText: "ALT אוטומטי לתמונות (AI)",
    aiColorSuggestions: "הצעות צבע/קונטרסט (AI)",
  };
  return map[key] || key;
}

export default function Plans() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [error, setError] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payUrl, setPayUrl] = useState("");
  const [payTxId, setPayTxId] = useState("");
  const [payStatus, setPayStatus] = useState("idle"); // idle | pending | completed | failed

  useEffect(() => {
    let alive = true;
    api
      .get("/api/plans")
      .then(({ data }) => {
        if (!alive) return;
        setPlans(data.plans || []);
      })
      .catch((err) => setError(err?.response?.data?.message || "שגיאה"));
    api
      .get("/api/addons")
      .then(({ data }) => {
        if (!alive) return;
        setAddons(data.addons || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const openPayment = ({ iframeUrl, paymentUrl, providerTransactionId }) => {
    setPayUrl(iframeUrl || paymentUrl || "");
    setPayTxId(providerTransactionId || "");
    setPayStatus("pending");
    setPayOpen(true);
  };

  const buy = async (planId) => {
    setError("");
    try {
      const { data } = await api.post("/api/payment/create-transaction", { planId });
      openPayment(data);
    } catch (err) {
      const msg = err?.response?.data?.message;
      const userMessage = err?.response?.data?.userMessage;
      const providerError = err?.response?.data?.providerError;
      const hint = err?.response?.data?.hint;
      
      // Use server's user-friendly message if provided
      if (userMessage) {
        setError(userMessage + (providerError ? `\n\nפרטים: ${providerError}` : ""));
      } else if (msg === "payment_auth_failed") {
        setError(`שגיאת אימות עם ספק התשלום. יש לבדוק את הגדרות ה-API בשרת.\n${hint || ""}\n${providerError ? `שגיאה: ${providerError}` : ""}`);
      } else if (msg === "payment_not_configured" || msg === "smartpay_not_configured") {
        setError("מערכת התשלום בהגדרה. בינתיים נוכל להפעיל את השירות ידנית. צור קשר בתמיכה.");
      } else if (msg === "payment_provider_unavailable" || msg === "smartpay_checkout_failed") {
        setError(`שירות התשלום זמנית לא זמין. אנא נסה שוב בעוד מספר דקות או פנה לתמיכה.${providerError ? `\n\nשגיאה טכנית: ${providerError}` : ""}`);
      } else if (msg === "smartpay_provider_error") {
        const rid = err?.response?.data?.providerRequestId;
        setError(`שירות התשלום נתקל בבעיה. נסה שוב מאוחר יותר.${rid ? ` (Request ID: ${rid})` : ""}`);
      } else {
        setError(msg || "שגיאה ביצירת תשלום. אנא נסה שוב או צור קשר בתמיכה.");
      }
      
      console.error("Payment error details:", {
        message: msg,
        status: err?.response?.status,
        data: err?.response?.data,
        fullError: err
      });
    }
  };

  const buyAddon = async (addonCode) => {
    setError("");
    try {
      const { data } = await api.post("/api/payment/create-transaction", { addonCode });
      openPayment(data);
    } catch (err) {
      const msg = err?.response?.data?.message;
      const userMessage = err?.response?.data?.userMessage;
      const providerError = err?.response?.data?.providerError;
      const hint = err?.response?.data?.hint;
      
      // Use server's user-friendly message if provided
      if (userMessage) {
        setError(userMessage + (providerError ? `\n\nפרטים: ${providerError}` : ""));
      } else if (msg === "payment_auth_failed") {
        setError(`שגיאת אימות עם ספק התשלום. יש לבדוק את הגדרות ה-API בשרת.\n${hint || ""}\n${providerError ? `שגיאה: ${providerError}` : ""}`);
      } else if (msg === "payment_not_configured" || msg === "smartpay_not_configured") {
        setError("מערכת התשלום בהגדרה. בינתיים נוכל להפעיל את השירות ידנית. צור קשר בתמיכה.");
      } else if (msg === "payment_provider_unavailable" || msg === "smartpay_checkout_failed") {
        setError(`שירות התשלום זמנית לא זמין. אנא נסה שוב בעוד מספר דקות או פנה לתמיכה.${providerError ? `\n\nשגיאה טכנית: ${providerError}` : ""}`);
      } else if (msg === "smartpay_provider_error") {
        const rid = err?.response?.data?.providerRequestId;
        setError(`שירות התשלום נתקל בבעיה. נסה שוב מאוחר יותר.${rid ? ` (Request ID: ${rid})` : ""}`);
      } else {
        setError(msg || "שגיאה ביצירת תשלום. אנא נסה שוב או צור קשר בתמיכה.");
      }
      
      console.error("Payment error details:", {
        message: msg,
        status: err?.response?.status,
        data: err?.response?.data,
        fullError: err
      });
    }
  };

  // Poll transaction status while payment dialog is open
  useEffect(() => {
    if (!payOpen || !payTxId || payStatus !== "pending") return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/api/payment/transaction/${encodeURIComponent(payTxId)}`);
        const st = String(data?.transaction?.status || "").toLowerCase();
        if (!alive) return;
        if (st === "completed") setPayStatus("completed");
        else if (st === "failed" || st === "cancelled" || st === "canceled") setPayStatus("failed");
      } catch {
        // ignore while pending (webhook may be delayed)
      }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [payOpen, payTxId, payStatus]);

  // Auto-close dialog after completion (give user feedback)
  useEffect(() => {
    if (!payOpen) return;
    if (payStatus !== "completed" && payStatus !== "failed") return;
    const t = setTimeout(() => {
      setPayOpen(false);
    }, 1200);
    return () => clearTimeout(t);
  }, [payOpen, payStatus]);

  const checkRow = (key, content) => (
    <Stack component="li" key={key} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.5 }}>
      <CheckRoundedIcon sx={{ fontSize: 18, color: "success.main", mt: 0.25, flex: "none" }} aria-hidden />
      <Typography variant="body2">{content}</Typography>
    </Stack>
  );

  return (
    <Box>
      <PageHeader title={t("p_plans_title")} subtitle={t("p_plans_subtitle", "בחרו את החבילה המתאימה לכם — אפשר לשדרג בכל עת")} />
      {error ? (
        <Alert severity="error" sx={{ mb: 2.5, whiteSpace: "pre-line" }}>
          {error}
        </Alert>
      ) : null}
      <Grid container spacing={2.5} alignItems="stretch">
        {plans.map((p) => {
          const feats = normalizeFeatures(p.features);
          return (
            <Grid item xs={12} md={6} lg={4} key={p.id}>
              <Card sx={{ height: "100%", display: "flex", flexDirection: "column", p: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <IconTile size={40} tone="primary">
                    <WorkspacePremiumOutlinedIcon />
                  </IconTile>
                  <Typography variant="h5" component="h2" sx={{ flex: 1 }}>
                    {p.name}
                  </Typography>
                  <Pill tone="neutral">{p.interval === "yearly" ? t("p_yearly") : t("p_monthly")}</Pill>
                </Stack>
                <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: "2.25rem", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1 }}>
                    {Number(p.price).toFixed(2)} ₪
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    / {p.interval === "yearly" ? t("p_yearly") : t("p_monthly")}
                  </Typography>
                </Stack>

                <Box sx={{ p: 1.75, borderRadius: 1.5, bgcolor: (th) => th.aw.soft, mb: 2, display: "grid", gap: 0.5 }}>
                  <Typography variant="body2">
                    {t("p_includes")} <b>{p.limits_sites}</b> {t("p_sites_label")}
                  </Typography>
                  <Typography variant="body2">
                    {t("p_includes")} <b>{Number(p.pageviews_included_monthly || 0).toLocaleString()}</b> {t("p_pageviews_label")}
                  </Typography>
                  <Typography variant="body2">
                    {Number(p.ai_credits_monthly || 0) > 0 ? (
                      <>
                        {t("p_includes")} <b>{p.ai_credits_monthly}</b> {t("p_ai_credits_label")}
                      </>
                    ) : (
                      t("p_no_ai_credits")
                    )}
                  </Typography>
                </Box>

                <Typography variant="overline" component="h3" sx={{ mb: 0.5 }}>
                  {t("p_features_label")}
                </Typography>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", mb: 2 }}>
                  {Object.entries(feats)
                    .filter(([, enabled]) => Boolean(enabled))
                    .map(([k]) => checkRow(k, featureLabel(k)))}
                  {Object.keys(feats).length === 0 ? (
                    <Typography component="li" variant="body2" sx={{ color: "text.secondary" }}>
                      —
                    </Typography>
                  ) : null}
                </Box>
                {Number(p.pageviews_overage_price_per_1000 || 0) > 0 ? (
                  <Typography variant="caption" component="p" sx={{ mt: "auto", mb: 2 }}>
                    {t("p_overage_traffic")} {Number(p.pageviews_overage_price_per_1000).toFixed(2)} ₪ {t("p_per_1000")} · {t("p_extra_site")}{" "}
                    {Number(p.extra_site_price_monthly || 0).toFixed(2)} ₪{t("p_per_month")}
                  </Typography>
                ) : (
                  <Box sx={{ mt: "auto" }} />
                )}
                <Button variant="contained" size="large" fullWidth onClick={() => buy(p.id)}>
                  {t("p_buy_button")}
                </Button>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {addons.length ? (
        <Box component="section" sx={{ mt: 5 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>
            {t("p_addons_title")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
            {t("p_addons_subtitle", "הרחבות חודשיות שאפשר להוסיף לכל חבילה")}
          </Typography>
          <Grid container spacing={2}>
            {addons.map((a) => (
              <Grid item xs={12} md={6} key={a.code}>
                <Card sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                  <IconTile size={40} tone="violet">
                    <ExtensionOutlinedIcon />
                  </IconTile>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>{a.name}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {Number(a.add_sites) > 0 ? `+${a.add_sites} ${t("p_sites_label")} · ` : ""}
                      {Number(a.add_pageviews_monthly) > 0 ? `+${Number(a.add_pageviews_monthly).toLocaleString()} ${t("p_pageviews_label")} · ` : ""}
                      {Number(a.add_ai_credits_monthly) > 0 ? `+${a.add_ai_credits_monthly} ${t("p_ai_credits_label")}` : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "end", flex: "none" }}>
                    <Typography sx={{ fontWeight: 800 }}>{Number(a.price).toFixed(2)} ₪</Typography>
                    <Typography variant="caption" component="div">
                      {t("p_monthly")}
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => buyAddon(a.code)} sx={{ flex: "none" }}>
                    {t("p_buy_button")}
                  </Button>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : null}

      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t("p_payment_title")}</DialogTitle>
        <DialogContent dividers>
          {payStatus === "completed" ? <Alert severity="success" sx={{ mb: 2 }}>{t("p_payment_success")}</Alert> : null}
          {payStatus === "failed" ? <Alert severity="error" sx={{ mb: 2 }}>{t("p_payment_failed")}</Alert> : null}
          {payStatus === "pending" ? <Alert severity="info" sx={{ mb: 2 }}>{t("p_payment_pending")}</Alert> : null}

          {payUrl ? (
            <Box sx={{ height: { xs: 520, md: 640 }, width: "100%", borderRadius: 1.5, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
              <iframe
                title="SmartPay payment"
                src={payUrl}
                style={{ width: "100%", height: "100%", border: 0 }}
                allow="payment *; clipboard-read; clipboard-write"
              />
            </Box>
          ) : (
            <Alert severity="warning">{t("p_payment_no_url")}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => (payUrl ? window.open(payUrl, "_blank", "noopener,noreferrer") : null)} disabled={!payUrl}>
            {t("p_payment_open_new_window")}
          </Button>
          <Button onClick={() => setPayOpen(false)}>{t("p_payment_close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


